import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  FileAudio,
  Loader2,
  Play,
  ShieldCheck,
  Square,
  UploadCloud,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils/cn';
import { voiceComposeKeys } from '@/features/voice-campaigns/api';
import { usePromptCategories, useUploadPrompt } from '@/features/voice-library/api';
import type { PromptCategory } from '@/features/voice-library/types';
import { registerDraftPrompt } from '../../lib/draft-prompts';

/** Same rule the API enforces (`isValidStringForAUD`). */
const NAME_RE = /^[a-zA-Z0-9_-]+$/;

interface Props {
  /** Called after a successful upload with the new prompt id (if the API
   *  returned one) so the caller can select it in the dropdown. */
  onUploaded: (result: { promptId?: string; fileName: string }) => void;
  onClose: () => void;
}

function formatDuration(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** Inline "upload a new prompt" panel for the TTS flow step editor.
 *  The prompt is saved as pending and goes to the Super Admin for approval
 *  together with the TTS flow. */
export function NewPromptUploader({ onUploaded, onClose }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const qc = useQueryClient();
  const upload = useUploadPrompt();
  const categoriesQuery = usePromptCategories();

  const categories = categoriesQuery.data ?? [];

  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [touched, setTouched] = useState(false);

  /* ----- local audio preview (object URL is always revoked) ----- */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const disposeAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setPlaying(false);
    setDuration(0);
  };
  useEffect(() => disposeAudio, []);

  const acceptFile = (f: File) => {
    const ext = f.name.toLowerCase().split('.').pop() ?? '';
    if (ext !== 'wav' && ext !== 'mp3') {
      toast.error('Only .wav or .mp3 files are supported.');
      return;
    }
    disposeAudio();
    setFile(f);
    // Auto-fill the name from the file, made safe for the API rule.
    setDisplayName((cur) =>
      cur ? cur : f.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]+/g, '_'),
    );
    const url = URL.createObjectURL(f);
    urlRef.current = url;
    const a = new Audio(url);
    a.addEventListener('loadedmetadata', () =>
      setDuration(Number.isFinite(a.duration) ? a.duration : 0),
    );
    a.addEventListener('ended', () => setPlaying(false));
    audioRef.current = a;
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      void a.play();
      setPlaying(true);
    }
  };

  const clearFile = () => {
    disposeAudio();
    setFile(null);
  };

  const nameTrimmed = displayName.trim();
  const nameError = !nameTrimmed
    ? 'Display name is required.'
    : !NAME_RE.test(nameTrimmed)
      ? 'Use only letters, digits, hyphen (-) and underscore (_).'
      : '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!file) return toast.error('Pick an audio file.');
    if (nameError) return toast.error(nameError);
    if (!category) return toast.error('Pick a category.');

    const ext = (file.name.toLowerCase().split('.').pop() ?? 'wav') as 'wav' | 'mp3';
    upload.mutate(
      {
        waveFile: file,
        userId: Number(userId),
        fileName: nameTrimmed,
        promptCategory: category as PromptCategory,
        fileType: ext,
        ttsFlowOnly: true,
      },
      {
        onSuccess: async (res) => {
          const promptId = (res as { promptId?: string } | undefined)?.promptId;
          if (!promptId) {
            // API answers 200 with only a message for rejections
            // (duplicate name, bad format, invalid category …).
            toast.error(res?.message ?? 'Could not upload the prompt.');
            return;
          }
          registerDraftPrompt(promptId);
          toast.success(
            res?.message ?? 'Prompt uploaded — it will be approved with this flow.',
          );
          // Step dropdown reads this query — refresh it before selecting.
          await qc.invalidateQueries({ queryKey: [...voiceComposeKeys.all, 'prompts'] });
          onUploaded({ promptId, fileName: nameTrimmed });
          clearFile();
          setDisplayName('');
          setCategory('');
          setTouched(false);
          onClose();
        },
        onError: () => toast.error('Could not upload the prompt.'),
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="relative overflow-hidden rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 via-background to-violet-50 p-3 shadow-sm dark:border-sky-900/50 dark:from-sky-950/30 dark:to-violet-950/20"
    >
      {/* accent stripe */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-sky-500 via-violet-500 to-fuchsia-500"
      />

      <div className="mb-2.5 flex items-start gap-2">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-500 text-white shadow-sm">
          <UploadCloud className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">Upload a new prompt</div>
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3 shrink-0 text-emerald-600" />
            Goes to your Super Admin for approval together with this TTS flow.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onClose}
          aria-label="Close upload panel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Drop zone / selected file */}
      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-2.5 dark:border-emerald-900/50 dark:bg-emerald-950/20">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-background"
            onClick={togglePlay}
            aria-label={playing ? 'Pause preview' : 'Play preview'}
          >
            {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <FileAudio className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
              <span className="truncate text-sm font-medium text-foreground">{file.name}</span>
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {(file.size / 1024).toFixed(0)} KB · {formatDuration(duration)}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={clearFile}
            disabled={upload.isPending}
            aria-label="Remove file"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) acceptFile(f);
          }}
          className={cn(
            'group flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-all duration-200',
            dragOver
              ? 'scale-[1.01] border-sky-500 bg-sky-500/10'
              : 'border-border bg-background/60 hover:border-sky-400 hover:bg-sky-500/5',
          )}
        >
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full bg-sky-500/10 text-sky-600 transition-transform duration-200 group-hover:-translate-y-0.5',
              dragOver && '-translate-y-1 bg-sky-500/20',
            )}
          >
            <UploadCloud className="h-5 w-5" />
          </div>
          <div className="text-sm font-semibold text-foreground">
            {dragOver ? 'Drop it here' : 'Drag & drop a WAV or MP3 file'}
          </div>
          <p className="text-xs text-muted-foreground">
            or <span className="font-medium text-sky-600 underline-offset-2 group-hover:underline">browse</span>{' '}
            from your computer
          </p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".wav,.mp3,audio/wav,audio/mpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) acceptFile(f);
          e.target.value = '';
        }}
      />

      {/* Metadata */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="tts-new-prompt-name" className="text-[11px]">
            Display name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="tts-new-prompt-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="e.g. welcome-en-male"
            className="h-8 text-xs"
            aria-invalid={touched && Boolean(nameError)}
          />
          {touched && nameError && (
            <p className="text-[10px] text-destructive">{nameError}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">
            Category <span className="text-destructive">*</span>
          </Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue
                placeholder={categoriesQuery.isLoading ? 'Loading…' : 'Pick a category'}
              />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.name)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Uploading shimmer */}
      {upload.isPending && (
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-950">
          <div className="h-full w-1/3 animate-pulse rounded-full bg-gradient-to-r from-sky-500 to-violet-500" />
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={upload.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={upload.isPending}>
          {upload.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UploadCloud className="h-4 w-4" />
          )}
          Upload &amp; use
        </Button>
      </div>
    </form>
  );
}